// Top-level imports
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import LoadingSpinner from './LoadingSpinner';
import MyPlanTab from './MyPlanTab';
import './SettingsPage.css';
import AppIcon from './icons/AppIcon';
import { API_ENDPOINTS } from '../config/constants';
const OPENWEATHER_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY || '';

// Mapeia nomes de estados do BR para UF corretas
const BR_STATE_NAME_TO_UF = {
  'acre': 'AC', 'alagoas': 'AL', 'amapa': 'AP', 'amapá': 'AP', 'amazonas': 'AM',
  'bahia': 'BA', 'ceara': 'CE', 'ceará': 'CE', 'distrito federal': 'DF', 'espirito santo': 'ES', 'espírito santo': 'ES',
  'goias': 'GO', 'goiás': 'GO', 'maranhao': 'MA', 'maranhão': 'MA', 'mato grosso': 'MT', 'mato grosso do sul': 'MS',
  'minas gerais': 'MG', 'para': 'PA', 'pará': 'PA', 'paraiba': 'PB', 'paraíba': 'PB', 'parana': 'PR', 'paraná': 'PR',
  'pernambuco': 'PE', 'piaui': 'PI', 'piauí': 'PI', 'rio de janeiro': 'RJ', 'rio grande do norte': 'RN',
  'rio grande do sul': 'RS', 'rondonia': 'RO', 'rondônia': 'RO', 'roraima': 'RR', 'santa catarina': 'SC',
  'sao paulo': 'SP', 'são paulo': 'SP', 'sergipe': 'SE', 'tocantins': 'TO'
};
const VALID_UF = new Set(['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']);
const mapToUF = (stateRaw) => {
  if (!stateRaw) return '';
  const s = String(stateRaw).trim();
  const up = s.toUpperCase();
  if (VALID_UF.has(up)) return up;
  const norm = s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  return BR_STATE_NAME_TO_UF[norm] || '';
};

const initialCompanyState = {
  nome_fantasia: '',
  cnpj: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  telefone_principal: '',
  telefone_secundario: '',
  email_contato: '',
  site: '',
  segmento: '',
  descricao: '',
  plano_contratado: 'GRATUITO',
  status_empresa: 'ATIVO',
};

const SettingsPage = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_superuser) || (parseInt(user?.nivel_acesso || 0, 10) >= 99);
  const [companyId, setCompanyId] = useState(null);
  const [form, setForm] = useState(initialCompanyState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('usuario');
  const [cepLoading, setCepLoading] = useState(false);
  const [userData, setUserData] = useState({
    nome_completo: '',
    nome_usuario: '',
    email: '',
    telefone: '',
    empresa: '',
    cidade: '',
    estado: '',
  });
  // Autocomplete de cidade (usuário)
  const [cityQuery, setCityQuery] = useState('');
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityDirty, setCityDirty] = useState(false); // true quando digitou e não confirmou uma seleção
  const [userGeo, setUserGeo] = useState({ lat: null, lon: null });
  const [userSaving, setUserSaving] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    current: '',
    next: '',
    confirm: '',
  });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdVisible, setPwdVisible] = useState({ current: false, next: false, confirm: false });

  // Modal: Adicionar Usuário
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [creatingUser, setCreatingUser] = useState(false);

  // Modal: Listar Usuários
  const [showListUsersModal, setShowListUsersModal] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [waCloudForm, setWaCloudForm] = useState({
    connection_name: 'WhatsApp Cloud (Meta)',
    phone_number_id: '',
    access_token: '',
    display_phone_number: '',
    waba_id: '',
  });
  const [waCloudStatus, setWaCloudStatus] = useState(null);
  const [waCloudSaving, setWaCloudSaving] = useState(false);

  const canLoad = useMemo(() => !!user?.company_id, [user?.company_id]);
  const companyIdFromContext = user?.company_id || null;

  const fetchCompanyId = useCallback(async () => {
    // Agora usamos apenas o contexto/localStorage
    return user?.company_id || null;
  }, [user?.company_id]);

  const COMPANY_KEY = (cid) => `astro_company_settings_${cid}`;
  const fetchCompanySettings = useCallback(async (cid) => {
    // Busca prioritariamente no Supabase, com fallback opcional ao localStorage
    try {
      const { data, error } = await supabase
        .from('configuracoes_empresa')
        .select(
          [
            'company_id',
            'nome_fantasia',
            'cnpj',
            'logradouro',
            'numero',
            'complemento',
            'bairro',
            'cidade',
            'estado',
            'cep',
            'telefone_principal',
            'telefone_secundario',
            'email_contato',
            'site',
            'segmento',
            'descricao',
            'plano_contratado',
            'status_empresa',
          ].join(', ')
        )
        .eq('company_id', cid)
        .single();
      if (error) {
        console.warn('[SettingsPage] Supabase configuracoes_empresa erro:', error);
      }
      if (data) return data;
    } catch (err) {
      console.warn('[SettingsPage] Falha ao consultar Supabase configuracoes_empresa:', err);
    }
    // Fallback local (opcional)
    try {
      const raw = localStorage.getItem(COMPANY_KEY(cid));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed || null;
    } catch (_) {
      return null;
    }
  }, []);

  // Remover esta linha:
  // const canLoad = useMemo(() => !!user?.company_id, [user?.company_id]);
  
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        console.debug('[SettingsPage] Iniciando carregamento.', {
          userId: user?.id || null,
          companyIdFromContext,
        });
        // Aguarda presença de user.id para garantir sessão resolvida
        if (!user?.id) {
          console.debug('[SettingsPage] user.id indisponível ainda; aguardando próxima atualização de contexto.');
          setLoading(false);
          return;
        }
        // Prioriza o company_id já disponível no contexto; se não houver, tenta fallback em profiles
        let cid = companyIdFromContext;
        if (!cid) {
          try {
            cid = await fetchCompanyId();
          } catch (e) {
            console.warn('Falha ao obter company_id via profiles:', e?.message || e);
          }
        }
        if (!isMounted) return;
        if (!cid) {
          // Sem company_id ainda: não trata como erro; apenas exibe aviso e encerra ciclo
          console.debug('[SettingsPage] company_id indisponível. Encerrando ciclo sem erro.');
          setLoading(false);
          return;
        }
        setCompanyId(cid);
        console.debug('[SettingsPage] company_id definido:', cid, '— buscando configuracoes_empresa (localStorage)...');
        const settings = await fetchCompanySettings(cid);
        if (!isMounted) return;
        console.debug('[SettingsPage] configuracoes_empresa recebido:', settings);
        setForm({ ...initialCompanyState, ...(settings || {}) });
        console.debug('[SettingsPage] formulário atualizado com dados da empresa.');
      } catch (e) {
        if (!isMounted) return;
        console.error('Erro ao carregar configurações:', e);
        setError(e.message || 'Erro ao carregar configurações');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [fetchCompanyId, fetchCompanySettings, companyIdFromContext, user?.id]); // observar mudanças em user.id também

  useEffect(() => {
    if (location.state?.openEmpresaTab) {
      setActiveTab('empresa');
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    async function loadWa() {
      if (!companyId || !user?.id || activeTab !== 'empresa') return;
      try {
        const params = new URLSearchParams({ userId: user.id, companyId });
        const r = await fetch(`${API_ENDPOINTS.COMPANY_WHATSAPP_CLOUD}?${params.toString()}`, {
          credentials: 'include',
        });
        const j = await r.json();
        if (cancelled || !j.ok) return;
        setWaCloudStatus(j);
        setWaCloudForm((f) => ({
          ...f,
          phone_number_id: j.phone_number_id || '',
          display_phone_number: j.display_number || '',
        }));
      } catch (_) {}
    }
    loadWa();
    return () => { cancelled = true; };
  }, [companyId, user?.id, activeTab]);

  const handleSaveWhatsappCloud = async (e) => {
    e?.preventDefault?.();
    if (!user?.id || !companyId) return;
    setWaCloudSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await fetch(API_ENDPOINTS.COMPANY_WHATSAPP_CLOUD, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId: user.id,
          companyId,
          access_token: waCloudForm.access_token,
          phone_number_id: waCloudForm.phone_number_id,
          display_phone_number: waCloudForm.display_phone_number || null,
          connection_name: waCloudForm.connection_name || 'WhatsApp Cloud (Meta)',
          waba_id: waCloudForm.waba_id || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `Erro ${resp.status}`);
      setSuccess(
        'WhatsApp Cloud salvo na empresa. No Meta: URL …/webhooks/meta-whatsapp e o mesmo texto em "Verificar token" e em META_WHATSAPP_WEBHOOK_VERIFY_TOKEN no servidor (não use o token EAAT). Ver docs/META_WHATSAPP_WEBHOOK.md.'
      );
      setWaCloudForm((f) => ({ ...f, access_token: '' }));
      const params = new URLSearchParams({ userId: user.id, companyId });
      const r2 = await fetch(`${API_ENDPOINTS.COMPANY_WHATSAPP_CLOUD}?${params.toString()}`, { credentials: 'include' });
      const j2 = await r2.json();
      if (j2.ok) setWaCloudStatus(j2);
    } catch (err) {
      setError(err.message || 'Erro ao salvar WhatsApp Cloud');
    } finally {
      setWaCloudSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buscarEnderecoPorCEP = async (cep) => {
    try {
      const onlyDigits = cep.replace(/\D/g, '');
      if (onlyDigits.length !== 8) return;
      setCepLoading(true);
      const res = await fetch(`https://viacep.com.br/ws/${onlyDigits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((prev) => ({
          ...prev,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
          cep: onlyDigits.replace(/(\d{5})(\d{3})/, '$1-$2'),
        }));
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    } finally {
      setCepLoading(false);
    }
  };

  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === 'cnpj') {
      processed = value.replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/\.(\d{3})(\d)/, '.$1.$2')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    if (name === 'cep') {
      processed = value.replace(/\D/g, '');
      if (processed.length > 5) processed = processed.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
      if (processed.replace(/\D/g, '').length === 8) buscarEnderecoPorCEP(processed);
    }
    setForm((prev) => ({ ...prev, [name]: processed }));
  };

  useEffect(() => {
    // Carrega dados do perfil do usuário priorizando Supabase; fallback para cache local
    const loadUserProfile = async () => {
      if (activeTab !== 'usuario' || !user?.id) return;
      try {
        // Fallback local (para cidade/estado até existirem no Supabase)
        let localParsed = {};
        try {
          const raw = localStorage.getItem(`astro_user_profile_${user.id}`);
          localParsed = raw ? JSON.parse(raw) : {};
        } catch (_) {}

        // Busca perfil no Supabase (dados existentes)
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, nome, email, company_id, nome_usuario, telefone, cidade, estado, ativo')
          .eq('id', user.id)
          .single();
        if (profileError) {
          console.warn('Falha ao carregar profile no Supabase:', profileError);
        }

        // Resolve nome da empresa via configuracoes_empresa
        let empresaNome = '';
        if (profile?.company_id) {
          try {
            const { data: emp, error: empErr } = await supabase
              .from('configuracoes_empresa')
              .select('nome_fantasia')
              .eq('company_id', profile.company_id)
              .single();
            if (!empErr && emp?.nome_fantasia) empresaNome = emp.nome_fantasia;
          } catch (e) {
            console.warn('Falha ao carregar nome da empresa:', e);
          }
        }

        setUserData((prev) => ({
          ...prev,
          email: profile?.email || user.email || '',
          nome_completo: profile?.nome || '',
          empresa: empresaNome || prev.empresa || '',
          nome_usuario: profile?.nome_usuario ?? localParsed?.nome_usuario ?? prev.nome_usuario ?? '',
          telefone: profile?.telefone ?? localParsed?.telefone ?? prev.telefone ?? '',
          cidade: profile?.cidade ?? localParsed?.cidade ?? prev.cidade ?? '',
          estado: profile?.estado ?? localParsed?.estado ?? prev.estado ?? '',
        }));
        // Ajusta query inicial e geo do cache
        try {
          setCityQuery(profile?.cidade || localParsed?.cidade || '');
          const rawGeo = localStorage.getItem(`astro_profile_geo_${user.id}`);
          if (rawGeo) {
            const parsedGeo = JSON.parse(rawGeo);
            if (parsedGeo && (parsedGeo.lat || parsedGeo.lon)) {
              setUserGeo({ lat: parsedGeo.lat ?? null, lon: parsedGeo.lon ?? null });
            }
          }
        } catch (_) {}
      } catch (e) {
        console.warn('Falha ao carregar perfil do usuário:', e);
      }
    };
    loadUserProfile();
  }, [activeTab, user?.id, user?.email]);

  // Autocomplete: buscar cidades conforme digita (Brasil)
  useEffect(() => {
    let abort = false;
    const timer = setTimeout(async () => {
      if (activeTab !== 'usuario') return;
      // Só busca se cityDirty for true (usuário digitou)
      if (!cityDirty) {
        if (!abort) setCityOptions([]);
        return;
      }
      const q = (cityQuery || '').trim();
      if (q.length < 2 || !OPENWEATHER_KEY) {
        if (!abort) setCityOptions([]);
        return;
      }
      try {
        setCityLoading(true);
        const url = `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(q)},BR&limit=5&appid=${OPENWEATHER_KEY}`;
        const res = await fetch(url);
        const data = await res.json();
        if (abort) return;
        const opts = Array.isArray(data)
          ? data
              .filter((it) => (it?.country || '').toUpperCase() === 'BR')
              .map((it) => ({
                name: it?.name || '',
                uf: mapToUF(it?.state || ''),
                lat: it?.lat ?? null,
                lon: it?.lon ?? null,
              }))
          : [];
        setCityOptions(opts);
      } catch (_) {
        if (!abort) setCityOptions([]);
      } finally {
        if (!abort) setCityLoading(false);
      }
    }, 300);
    return () => { abort = true; clearTimeout(timer); };
  }, [cityQuery, activeTab, cityDirty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { 
        company_id: companyId, 
        ...form,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('configuracoes_empresa')
        .upsert(payload, { onConflict: 'company_id' });
      if (error) {
        throw error;
      }
      // Recarrega dados do Supabase para garantir sincronização
      const fresh = await fetchCompanySettings(companyId);
      if (fresh) {
        setForm((prev) => ({ ...prev, ...fresh }));
        try { localStorage.setItem(COMPANY_KEY(companyId), JSON.stringify(fresh)); } catch (_) {}
      } else {
        // cache local opcional caso não venha dado novo
        try { localStorage.setItem(COMPANY_KEY(companyId), JSON.stringify(payload)); } catch (_) {}
      }
      setSuccess('Configurações da empresa salvas e recarregadas do Supabase.');
    } catch (e) {
      console.error('Erro ao salvar configurações:', e);
      setError(e.message || 'Erro ao salvar configurações no Supabase');
    } finally {
      setSaving(false);
    }
  };

  const handleUserSave = async () => {
    if (!user?.id) return;
    if (cityDirty) {
      setError('Selecione uma cidade da lista para continuar.');
      return;
    }
    setUserSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // validação simples de UF
      const uf = (userData.estado || '').toUpperCase();
      const updatePayload = {
        nome: userData.nome_completo || '',
        nome_usuario: userData.nome_usuario || null,
        email: userData.email || null,
        telefone: userData.telefone || null,
        cidade: userData.cidade || null,
        estado: uf ? uf.slice(0, 2) : null,
        updated_at: new Date().toISOString(),
      };
      const { error: upErr } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', user.id);
      if (upErr) throw upErr;

      // Atualiza cache local (opcional)
      try { localStorage.setItem(`astro_user_profile_${user.id}`, JSON.stringify(updatePayload)); } catch (_) {}
      // Salva geo (lat/lon) localmente para o WeatherCard
      try {
        if (userGeo && userGeo.lat != null && userGeo.lon != null) {
          localStorage.setItem(`astro_profile_geo_${user.id}`, JSON.stringify({ lat: userGeo.lat, lon: userGeo.lon }));
        } else {
          localStorage.removeItem(`astro_profile_geo_${user.id}`);
        }
      } catch (_) {}

      // Sucesso silencioso: não exibe mensagem específica
    } catch (e) {
      console.error('Erro ao salvar usuário no Supabase:', e);
      setError(e.message || 'Erro ao salvar dados do usuário');
    } finally {
      setUserSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!user?.email) {
      setError('Usuário sem e-mail associado.');
      return;
    }
    setError(null);
    setSuccess(null);
    // validações básicas
    if (!pwdForm.current || !pwdForm.next || !pwdForm.confirm) {
      setError('Preencha todos os campos de senha.');
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setError('A nova senha e a confirmação não coincidem.');
      return;
    }
    if (pwdForm.next.length < 6) {
      setError('A nova senha deve ter no mínimo 6 caracteres.');
      return;
    }
    setPwdSaving(true);
    try {
      // Verifica senha atual
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email,
        password: pwdForm.current,
      });
      if (signInError) {
        throw new Error('Senha atual incorreta.');
      }
      // Atualiza a senha
      const { error: updateError } = await supabase.auth.updateUser({ password: pwdForm.next });
      if (updateError) {
        throw updateError;
      }
      // Sincroniza sessão/usuário (mantém conectado e atualizado)
      try { await supabase.auth.getUser(); } catch (_) {}
      setSuccess('Senha alterada com sucesso.');
      setPwdForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      console.error('Erro ao alterar senha:', e);
      setError(e.message || 'Erro ao alterar senha.');
    } finally {
      setPwdSaving(false);
    }
  };

  // Função para buscar todos os usuários da empresa
  const fetchCompanyUsers = async () => {
    if (!companyId) {
      console.log('❌ companyId não disponível:', companyId);
      return;
    }
    console.log('🔍 Buscando usuários da empresa:', companyId);
    setLoadingUsers(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, nome_usuario, email, telefone, cidade, estado, created_at, nivel_acesso')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ Erro ao buscar usuários:', error);
        throw error;
      }
      
      console.log('✅ Usuários encontrados:', data?.length || 0, data);
      setUsersList(data || []);
    } catch (e) {
      console.error('❌ Erro ao buscar usuários:', e);
      setError('Erro ao carregar lista de usuários: ' + e.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'usuario' ? 'active' : ''}`}
          onClick={() => setActiveTab('usuario')}
        >
          Usuário
        </button>
        <button
          className={`tab-button ${activeTab === 'meu-plano' ? 'active' : ''}`}
          onClick={() => setActiveTab('meu-plano')}
        >
          Meu Plano
        </button>
        {companyId && (
          <button
            type="button"
            className={`tab-button ${activeTab === 'empresa' ? 'active' : ''}`}
            onClick={() => setActiveTab('empresa')}
          >
            Empresa
          </button>
        )}
      </div>

      {/* Modal: Adicionar Usuario */}
      {showAddUserModal && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => !creatingUser && setShowAddUserModal(false)}
        >
          <div
            className="modal-content"
            style={{ background: 'var(--card-background)', border: '1px solid rgba(49,117,177,0.35)', borderRadius: 12, width: 'min(720px, 96vw)', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Adicionar Usuario</h3>
              <button className="view-all-button" onClick={() => !creatingUser && setShowAddUserModal(false)}>Fechar</button>
            </div>
            <div className="card-content">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nu_email">E-mail</label>
                  <input
                    id="nu_email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nu_password">Senha</label>
                  <input
                    id="nu_password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="nu_confirm_password">Confirmar Senha</label>
                  <input
                    id="nu_confirm_password"
                    type="password"
                    value={newUser.confirmPassword}
                    onChange={(e) => setNewUser(p => ({ ...p, confirmPassword: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="view-all-button" onClick={() => setShowAddUserModal(false)} disabled={creatingUser}>Cancelar</button>
                <button
                  className="save-button"
                  disabled={creatingUser || !companyId || !newUser.email || !newUser.password}
                  onClick={async () => {
                    if (!companyId) return;
                    setCreatingUser(true);
                    setError(null);
                    setSuccess(null);
                    try {
                      // Validação básica de senha (UI): presença e igualdade
                      if (!newUser.password || !newUser.confirmPassword) {
                        throw new Error('Informe a senha e a confirmação.');
                      }
                      if (newUser.password.length < 6) {
                        throw new Error('A senha deve ter no mínimo 6 caracteres.');
                      }
                      if (newUser.password !== newUser.confirmPassword) {
                        throw new Error('As senhas não coincidem.');
                      }
                      // Chamar backend seguro para criar usuário vinculado à empresa
                      const adminToken = import.meta.env.VITE_ADMIN_ACCESS_TOKEN;
                      const apiBase = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
                      const endpoint = apiBase ? `${apiBase}/admin/users` : '/admin/users';
                      const resp = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(adminToken ? { 'X-Admin-Token': adminToken } : {}),
                        },
                        body: JSON.stringify({
                          email: newUser.email,
                          password: newUser.password,
                          company_id: companyId,
                          nome: 'Usuário', // Nome padrão
                          redirectTo: window.location.origin + '/login',
                        }),
                      });
                      if (!resp.ok) {
                        const errJson = await resp.json().catch(() => ({}));
                        throw new Error(errJson?.error || `HTTP ${resp.status}`);
                      }
                      const out = await resp.json();
                      setShowAddUserModal(false);
                      setNewUser({ email: '', password: '', confirmPassword: '' });

                      // Vincular usuário à empresa
                      const userId = out.user?.id || out.user_id;
                      if (out.ok && userId) {
                        console.log('Tentando vincular usuário à empresa:', { userId, companyId });

                        // Primeiro, buscar se o perfil já existe
                        try {
                          const { data: existingProfile, error: profileError } = await supabase
                            .from('profiles')
                            .select('id, company_id')
                            .eq('id', userId)
                            .maybeSingle();

                          if (profileError) {
                            console.error('Erro ao buscar perfil existente:', profileError);
                          }

                          if (existingProfile) {
                            // Perfil existe, apenas atualizar company_id
                            console.log('Perfil encontrado, atualizando company_id');
                            const { data: updatedProfile, error: updateError } = await supabase
                              .from('profiles')
                              .update({
                                company_id: companyId,
                                updated_at: new Date().toISOString()
                              })
                              .eq('id', userId)
                              .select()
                              .single();

                            if (updateError) {
                              console.error('Erro ao atualizar company_id:', updateError);
                            } else {
                              console.log('Company ID atualizado com sucesso:', updatedProfile);
                            }
                          } else {
                            // Perfil não existe, criar via API
                            console.log('Perfil não encontrado, criando via API');
                            const linkResp = await fetch(endpoint.replace('/users', '/set-user-company'), {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                ...(adminToken ? { 'X-Admin-Token': adminToken } : {}),
                              },
                              body: JSON.stringify({
                                user_id: userId,
                                company_id: companyId,
                              }),
                            });

                            console.log('Resposta da criação:', linkResp.status, linkResp.statusText);

                            if (linkResp.ok) {
                              const linkData = await linkResp.json();
                              console.log('Perfil criado e vinculado com sucesso:', linkData);
                            } else {
                              const errorData = await linkResp.json().catch(() => ({}));
                              console.warn('Erro ao criar perfil:', errorData);
                            }
                          }
                        } catch (linkErr) {
                          console.warn('Falha na vinculação:', linkErr);
                        }
                      } else {
                        console.log('Não foi possível vincular - resposta inválida:', out);
                      }

                      setSuccess(out?.invited ? 'Usuário criado e convite enviado por e-mail.' : 'Usuário criado com sucesso.');
                    } catch (e) {
                      console.error('Erro ao criar usuário:', e);
                      // Mensagens mais amigáveis
                      const msg = String(e?.message || '').toLowerCase();
                      if (msg.includes('user already registered') || msg.includes('already registered')) {
                        setError('E-mail já está registrado.');
                      } else if (msg.includes('password')) {
                        setError(e.message);
                      } else {
                        setError(e.message || 'Erro ao criar usuário');
                      }
                    } finally {
                      setCreatingUser(false);
                    }
                  }}
                >
                  {creatingUser ? 'Salvando...' : 'Salvar Usuário'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Listar Usuários */}
      {showListUsersModal && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowListUsersModal(false)}
        >
          <div
            className="modal-content"
            style={{ background: 'var(--card-background)', border: '1px solid rgba(49,117,177,0.35)', borderRadius: 12, width: 'min(900px, 96vw)', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Usuários da Empresa</h3>
              <button className="view-all-button" onClick={() => setShowListUsersModal(false)}>Fechar</button>
            </div>
            <div className="card-content">
              {loadingUsers ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <LoadingSpinner message="Carregando usuários..." />
                </div>
              ) : usersList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  Nenhum usuário encontrado.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(49,117,177,0.3)' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-color)' }}>Nome</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-color)' }}>Usuário</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-color)' }}>Email</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-color)' }}>Telefone</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-color)' }}>Cidade/Estado</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-color)' }}>Nível</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersList.map((usr) => (
                        <tr key={usr.id} style={{ borderBottom: '1px solid rgba(49,117,177,0.15)' }}>
                          <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>{usr.nome || '-'}</td>
                          <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>{usr.nome_usuario || '-'}</td>
                          <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>{usr.email || '-'}</td>
                          <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>{usr.telefone || '-'}</td>
                          <td style={{ padding: '0.75rem', color: 'var(--text-color)' }}>
                            {usr.cidade && usr.estado ? `${usr.cidade}/${usr.estado}` : usr.cidade || usr.estado || '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-color)' }}>
                            {usr.nivel_acesso || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(68, 225, 255, 0.1)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                <strong>Total:</strong> {usersList.length} usuário(s) cadastrado(s)
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Carregando configurações..." />
      )}
      
      {!loading && !companyId && (
        <div className="info">Aguardando dados da empresa…</div>
      )}
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {!loading && companyId && (
        <div className="settings-form">
        {activeTab === 'empresa' && (
          <div className="settings-tab-content">
            <h3>Informações da Empresa</h3>
            <div className="settings-form">
              <h4>Informações Básicas</h4>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="nome_fantasia">Nome da Empresa *</label>
                  <input
                    type="text"
                    id="nome_fantasia"
                    name="nome_fantasia"
                    value={form.nome_fantasia}
                    onChange={handleCompanyInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cnpj">CNPJ</label>
                  <input
                    type="text"
                    id="cnpj"
                    name="cnpj"
                    value={form.cnpj || ''}
                    onChange={handleCompanyInputChange}
                    placeholder="00.000.000/0000-00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="cep">CEP</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="cep"
                    name="cep"
                    value={form.cep || ''}
                    onChange={handleCompanyInputChange}
                    onBlur={(e) => {
                      const digits = (e.target.value || '').replace(/\D/g, '');
                      if (digits.length === 8) buscarEnderecoPorCEP(e.target.value);
                    }}
                    placeholder="00000-000"
                    maxLength={9}
                    disabled={cepLoading}
                    style={{ paddingRight: cepLoading ? '40px' : '15px' }}
                  />
                  {cepLoading && (
                    <div className="loading-indicator" />
                  )}
                </div>
                <small className="form-text">Digite o CEP para preencher o endereço automaticamente</small>
              </div>

              <div className="form-group">
                <label htmlFor="logradouro">Rua</label>
                <input
                  type="text"
                  id="logradouro"
                  name="logradouro"
                  value={form.logradouro || ''}
                  onChange={handleCompanyInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="numero">Número</label>
                  <input
                    type="text"
                    id="numero"
                    name="numero"
                    value={form.numero || ''}
                    onChange={handleCompanyInputChange}
                  />
                </div>

                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="complemento">Complemento</label>
                  <input
                    type="text"
                    id="complemento"
                    name="complemento"
                    value={form.complemento || ''}
                    onChange={handleCompanyInputChange}
                  />
                </div>

                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="bairro">Bairro</label>
                  <input
                    type="text"
                    id="bairro"
                    name="bairro"
                    value={form.bairro || ''}
                    onChange={handleCompanyInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="cidade">Cidade</label>
                  <input
                    type="text"
                    id="cidade"
                    name="cidade"
                    value={form.cidade || ''}
                    onChange={handleCompanyInputChange}
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="estado">Estado (UF)</label>
                  <input
                    type="text"
                    id="estado"
                    name="estado"
                    value={form.estado || ''}
                    onChange={handleCompanyInputChange}
                    maxLength={2}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
              </div>

              <h4>Contato</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telefone_principal">Telefone Principal *</label>
                  <input
                    type="text"
                    id="telefone_principal"
                    name="telefone_principal"
                    value={form.telefone_principal || ''}
                    onChange={handleCompanyInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="telefone_secundario">Telefone Secundário</label>
                  <input
                    type="text"
                    id="telefone_secundario"
                    name="telefone_secundario"
                    value={form.telefone_secundario || ''}
                    onChange={handleCompanyInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="email_contato">E-mail *</label>
                  <input
                    type="email"
                    id="email_contato"
                    name="email_contato"
                    value={form.email_contato || ''}
                    onChange={handleCompanyInputChange}
                    required
                  />
                </div>

                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="site">Website</label>
                  <input
                    type="url"
                    id="site"
                    name="site"
                    value={form.site || ''}
                    onChange={handleCompanyInputChange}
                    placeholder="https://"
                  />
                </div>
              </div>

              <h4>WhatsApp Cloud API (Meta)</h4>
              <p style={{ fontSize: 13, opacity: 0.9, marginBottom: '0.75rem' }}>
                Token e Phone Number ID ficam gravados na empresa. Uma conexão aparece automaticamente em Conexões e em Atendimentos.
              </p>
              {waCloudStatus?.configured && (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                  Status: <strong>configurado</strong>
                  {waCloudStatus.token_preview ? ` — token atual: ${waCloudStatus.token_preview}` : ''}
                </p>
              )}
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="wa_cloud_name">Nome da conexão (lista)</label>
                  <input
                    id="wa_cloud_name"
                    type="text"
                    value={waCloudForm.connection_name}
                    onChange={(e) => setWaCloudForm((f) => ({ ...f, connection_name: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="wa_phone_number_id">Phone Number ID (Meta) *</label>
                  <input
                    id="wa_phone_number_id"
                    type="text"
                    value={waCloudForm.phone_number_id}
                    onChange={(e) => setWaCloudForm((f) => ({ ...f, phone_number_id: e.target.value }))}
                    placeholder="ID do número no Graph API"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="wa_token">Token de acesso (Bearer) *</label>
                  <input
                    id="wa_token"
                    type="password"
                    autoComplete="off"
                    value={waCloudForm.access_token}
                    onChange={(e) => setWaCloudForm((f) => ({ ...f, access_token: e.target.value }))}
                    placeholder={waCloudStatus?.configured ? 'Cole apenas se for trocar o token' : 'Cole o token do Meta'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="wa_display">Número exibido (opcional)</label>
                  <input
                    id="wa_display"
                    type="text"
                    value={waCloudForm.display_phone_number}
                    onChange={(e) => setWaCloudForm((f) => ({ ...f, display_phone_number: e.target.value }))}
                    placeholder="5511999999999"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="wa_waba">WABA ID (opcional)</label>
                  <input
                    id="wa_waba"
                    type="text"
                    value={waCloudForm.waba_id}
                    onChange={(e) => setWaCloudForm((f) => ({ ...f, waba_id: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-actions" style={{ marginBottom: '1rem' }}>
                <button
                  type="button"
                  className="save-button"
                  disabled={
                    waCloudSaving
                    || !waCloudForm.phone_number_id?.trim()
                    || (!waCloudForm.access_token?.trim() && !waCloudStatus?.configured)
                  }
                  onClick={handleSaveWhatsappCloud}
                >
                  {waCloudSaving ? 'Salvando…' : 'Salvar WhatsApp Cloud'}
                </button>
              </div>

              <h4>Informações Adicionais</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="segmento">Segmento de Atuação</label>
                  <input
                    type="text"
                    id="segmento"
                    name="segmento"
                    value={form.segmento || ''}
                    onChange={handleCompanyInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="plano_contratado">Plano Contratado</label>
                  <select
                    id="plano_contratado"
                    name="plano_contratado"
                    value={form.plano_contratado || 'GRATUITO'}
                    onChange={handleCompanyInputChange}
                    className="form-control"
                  >
                    <option value="GRATUITO">Gratuito</option>
                    <option value="BASICO">Básico</option>
                    <option value="PRO">Pro</option>
                    <option value="EMPRESARIAL">Empresarial</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="descricao">Descrição da Empresa</label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={form.descricao || ''}
                  onChange={handleCompanyInputChange}
                  rows={4}
                  placeholder="Descreva as atividades principais da sua empresa..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="save-button"
                  disabled={saving || !companyId}
                  onClick={(e) => { e.preventDefault(); handleSubmit(e); }}
                >
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usuario' && (
          <div className="settings-tab-content">
            <h3>Gerenciamento de Usuários</h3>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button className="add-user-button" onClick={() => { fetchCompanyUsers(); setShowListUsersModal(true); }}>
                <AppIcon name="group" className="plus-icon rotate-360" />
                Listar Usuários
              </button>
              <button className="add-user-button" onClick={() => setShowAddUserModal(true)}>
                <AppIcon name="add" className="plus-icon" />
                Adicionar Usuario
              </button>
            </div>
            <div className="settings-form">
              <h4>Informações do Usuário</h4>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label htmlFor="nome_completo">Nome Completo</label>
                  <input
                    type="text"
                    id="nome_completo"
                    name="nome_completo"
                    value={userData.nome_completo}
                    onChange={(e) => setUserData((p) => ({ ...p, nome_completo: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nome_usuario">Nome de Usuário</label>
                  <input
                    type="text"
                    id="nome_usuario"
                    name="nome_usuario"
                    value={userData.nome_usuario}
                    onChange={(e) => setUserData((p) => ({ ...p, nome_usuario: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={userData.email}
                    onChange={(e) => setUserData((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    type="text"
                    id="telefone"
                    name="telefone"
                    value={userData.telefone}
                    onChange={(e) => setUserData((p) => ({ ...p, telefone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="empresa">Empresa</label>
                  <input
                    type="text"
                    id="empresa"
                    name="empresa"
                    value={userData.empresa}
                    readOnly
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2, position: 'relative' }}>
                  <label htmlFor="cidade">Cidade</label>
                  <input
                    type="text"
                    id="cidade"
                    name="cidade"
                    value={cityQuery}
                    onChange={(e) => { setCityQuery(e.target.value); setCityDirty(true); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setCityOptions([]); }}
                    onBlur={() => { setTimeout(() => setCityOptions([]), 150); }}
                    autoComplete="off"
                  />
                  {cityLoading && <div className="loading-indicator" />}
                  {cityOptions.length > 0 && (
                    <ul className="autocomplete-list">
                      {cityOptions.map((opt, idx) => (
                        <li
                          key={`${opt.name}-${opt.uf || ''}-${idx}`}
                          className="autocomplete-item"
                          onMouseDown={() => {
                            setUserData((p) => ({ ...p, cidade: opt.name, estado: opt.uf || '' }));
                            setCityQuery(`${opt.name}`);
                            setCityDirty(false);
                            setUserGeo({ lat: opt.lat, lon: opt.lon });
                            setCityOptions([]);
                          }}
                        >
                          {opt.name} - {opt.uf || '—'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="estado">Estado (UF)</label>
                  <input
                    type="text"
                    id="estado"
                    name="estado"
                    value={userData.estado}
                    onChange={(e) => setUserData((p) => ({ ...p, estado: e.target.value.toUpperCase().slice(0,2) }))}
                    maxLength={2}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  className="save-button"
                  type="button"
                  onClick={handleUserSave}
                  disabled={userSaving || cityDirty}
                >
                  {userSaving ? 'Salvando...' : 'Salvar Dados'}
                </button>
                {/* Aba 'Usuários' removida */}
              </div>

              <h4 style={{ marginTop: 24 }}>Alterar Senha</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pwd_current">Senha atual</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={pwdVisible.current ? 'text' : 'password'}
                      id="pwd_current"
                      name="pwd_current"
                      value={pwdForm.current}
                      onChange={(e) => setPwdForm((p) => ({ ...p, current: e.target.value }))}
                      autoComplete="current-password"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setPwdVisible((v) => ({ ...v, current: !v.current }))}
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)' }}
                      className="btn-eye"
                    >
                      {pwdVisible.current ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="pwd_next">Nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={pwdVisible.next ? 'text' : 'password'}
                      id="pwd_next"
                      name="pwd_next"
                      value={pwdForm.next}
                      onChange={(e) => setPwdForm((p) => ({ ...p, next: e.target.value }))}
                      autoComplete="new-password"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setPwdVisible((v) => ({ ...v, next: !v.next }))}
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)' }}
                      className="btn-eye"
                    >
                      {pwdVisible.next ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="pwd_confirm">Confirmar nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={pwdVisible.confirm ? 'text' : 'password'}
                      id="pwd_confirm"
                      name="pwd_confirm"
                      value={pwdForm.confirm}
                      onChange={(e) => setPwdForm((p) => ({ ...p, confirm: e.target.value }))}
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setPwdVisible((v) => ({ ...v, confirm: !v.confirm }))}
                      style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)' }}
                      className="btn-eye"
                    >
                      {pwdVisible.confirm ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="save-button"
                  type="button"
                  onClick={handlePasswordChange}
                  disabled={pwdSaving}
                >
                  {pwdSaving ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'meu-plano' && (
          <MyPlanTab />
        )}
      </div>
      )}
    </div>
  );
};

export default SettingsPage;
